import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  User, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Flag,
  RefreshCw,
  Ban,
  Globe,
  Mail,
  ArrowLeft,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PlutoUser from './PlutoUser';

const PlutoSingleWithdrawal = () => {
  const [data, setData] = useState({});
  const [showUser, setShowUser] = useState(false); // Start collapsed on mobile
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [giftCardData, setGiftCardData] = useState({ code: '', pin: '' });
  const { referral_id, page, second_page, third_page } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    socket.emit('getWithdrawAdmin', {
      withdraw_id: third_page
    });
    
    socket.on('response_get_admin_withdraw', (data) => {
      setData(data);
      setLoading(false);
    });
    
    socket.on('reTakeWithdrawDet', () => {
      socket.emit('getWithdrawAdmin', {
        withdraw_id: third_page
      });
    });

    return () => {
      socket.off('response_get_admin_withdraw');
      socket.off('reTakeWithdrawDet');
    };
  }, [third_page]);

  const getStatusInfo = (withdraw) => {
    if (withdraw.paid === 1) {
      return { status: 'Paid', color: 'bg-green-500', textColor: 'text-green-100', icon: CheckCircle };
    }
    if (withdraw.flagged === 1) {
      return { status: 'Flagged', color: 'bg-red-500', textColor: 'text-red-100', icon: Flag };
    }
    if (withdraw.review === 1) {
      return { status: 'Review', color: 'bg-orange-500', textColor: 'text-orange-100', icon: Eye };
    }
    return { status: 'Processing', color: 'bg-blue-500', textColor: 'text-blue-100', icon: Clock };
  };

  const handleAction = (action) => {
    if (action === 'pay' && data.withdraw.type !== 'Crypto') {
      setShowActionModal(true);
    } else {
      executeAction(action);
    }
  };

  const executeAction = (action, additionalData = {}) => {
    const actions = {
      flag: () => socket.emit('adminAction', { action: 'flag', withdraw: data.withdraw.id }),
      review: () => socket.emit('adminAction', { action: 'review', withdraw: data.withdraw.id }),
      refund: () => socket.emit('adminAction', { action: 'refund_withdraw', withdraw: data.withdraw.id }),
      pay: () => socket.emit('adminAction', { 
        action: 'pay_gift_card', 
        withdraw: data.withdraw.id,
        ...additionalData 
      })
    };
    
    actions[action]?.();
    setShowActionModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-3">
        <div className="max-w-lg mx-auto space-y-3">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-700 rounded w-32 mb-2"></div>
            <div className="h-32 bg-gray-800 rounded-lg mb-3"></div>
            <div className="h-24 bg-gray-800 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const { withdraw, user } = data;
  const statusInfo = getStatusInfo(withdraw);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 p-3 flex items-center gap-3 z-10">
          <Link
            to={'/pluto/withdrawals'}
            className="p-1 hover:bg-gray-800 rounded transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-200 truncate">
              Withdrawal #{withdraw.id}
            </div>
            <div className="text-xs text-gray-400 truncate">
              {user.username} • {withdraw.type}
            </div>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color} ${statusInfo.textColor} flex items-center gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {statusInfo.status}
          </div>
        </div>

        <div className="p-3 space-y-3">
          {/* Amount Card - Ultra Compact */}
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xl font-bold text-gray-100">
                  ${parseFloat(parseInt(withdraw.amount) / 1000).toFixed(3)}
                </div>
                <div className="text-xs text-gray-400">Requested</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-300">
                  Fee: ${parseFloat(parseInt(withdraw.details?.fee || 0) / 1000).toFixed(3)}
                </div>
                <div className="text-xs text-gray-400">
                  {withdraw.paid === 1 ? '✅ Paid' : '⏳ Pending'}
                </div>
              </div>
            </div>

            {/* Payment Method - Single Line */}
            <div className="text-sm border-t border-gray-700 pt-2">
              <span className="text-gray-400">Method: </span>
              <span className="text-gray-200">{withdraw.option_request}</span>
            </div>

            {/* Key Details Toggle */}
            {/* <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full mt-2 text-xs text-gray-400 hover:text-gray-300 transition-colors flex items-center justify-center gap-1"
            > */}
              {/* {showDetails ? 'Hide Details' : 'Show Details'} */}
               {/* <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" /> */}
            {/* </button> */}

            {/* Collapsible Details */}
            {/* {showDetails && ( */}
              <div className="mt-3  border-t border-gray-700 space-y-2 text-sm">
                {withdraw.type === "Crypto" ? (
                  <>
                    <div>
                      <span className="text-gray-400">To: </span>
                      <span className="text-gray-200 text-xs break-all">{withdraw.withdraw_to}</span>
                    </div>
                    {/* <div>
                      <span className="text-gray-400">Price: </span>
                      <span className="text-gray-200">${parseFloat(withdraw.details?.cryptoPrice || 0).toFixed(3)}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Amount: </span>
                      <span className="text-gray-200">
                        {isNaN(parseFloat(withdraw.details?.paid)) 
                          ? parseFloat(withdraw.details?.value || 0).toFixed(8)
                          : parseFloat(withdraw.details?.paid).toFixed(8)
                        }
                      </span>
                    </div> */}
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-gray-400">Code: </span>
                      <span className="text-gray-200">{withdraw.details?.code || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Pin: </span>
                      <span className="text-gray-200">{withdraw.details?.pin || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Claimed: </span>
                      <span className="text-gray-200">{withdraw.details?.claimed || 'No'}</span>
                    </div>
                  </>
                )}
              </div>
            {/* )} */}

            {/* Flag Reason */}
            {withdraw.flagged === 1 && withdraw.flag_reason && (
              <div className="mt-2 p-2 bg-red-900/30 border border-red-800/50 rounded text-xs">
                <span className="text-red-300 font-medium">Flag: </span>
                <span className="text-red-200">{withdraw.flag_reason}</span>
              </div>
            )}
          </div>

          {/* Action Buttons - Mobile Optimized */}
          {withdraw.paid !== 1 && !user.banned && (
            <div className="grid grid-cols-2 gap-2">
              {withdraw.review !== 1 && (
                <>
                  {withdraw.type === "Crypto" ? (
                    <button
                      onClick={() => handleAction('flag')}
                      className={`p-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                        withdraw.flagged === 1 
                          ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                          : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      }`}
                    >
                      <Flag className="h-4 w-4" />
                      {withdraw.flagged === 1 ? 'Unflag' : 'Flag'}
                    </button>
                  ) : (
                    withdraw.flagged === 1 && (
                      <button
                        onClick={() => handleAction('pay')}
                        className="p-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center justify-center gap-1.5"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Pay
                      </button>
                    )
                  )}
                </>
              )}
              
              <button
                onClick={() => handleAction('review')}
                className="p-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center justify-center gap-1.5"
              >
                <Eye className="h-4 w-4" />
                {withdraw.review === 1 ? 'Release' : 'Review'}
              </button>
              
              <button
                onClick={() => handleAction('refund')}
                className="p-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                Refund
              </button>
            </div>
          )}

          {/* User Quick Info */}
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-600/20 rounded">
                  <User className="h-4 w-4 text-blue-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-200">{user.username}</div>
                  <div className="text-xs text-gray-400">ID: {user.id} • {user.country}</div>
                </div>
              </div>
              {/* <button
                onClick={() => setShowUser(!showUser)}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
              >
                {showUser ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button> */}
            </div>

            {/* Expandable User Details */}
            {/* {showUser && ( */}
              <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Email: </span>
                    <span className="text-gray-200 break-all">{user.emailProvider}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Phone: </span>
                    <span className="text-gray-200">{user.phoneCountry || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Referee: </span>
                    <span className="text-gray-200">{user.referee || 'None'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Status: </span>
                    <span className={user.banned ? 'text-red-400' : 'text-green-400'}>
                      {user.banned ? 'Banned' : 'Active'}
                    </span>
                  </div>
                </div>
                
                {user.countries && (
                  <div className="text-xs">
                    <span className="text-gray-400">Countries: </span>
                    <span className="text-gray-200">{user.countries}</span>
                  </div>
                )}

                {/* User Component */}
                <div className="pt-2 border-t border-gray-700">
                  <PlutoUser second_page={user.id} x={1} />
                </div>
              </div>
            {/* )} */}
          </div>
        </div>

        {/* Gift Card Modal - Mobile Optimized */}
        {showActionModal && (
          <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
            <div className="bg-gray-800 rounded-t-lg sm:rounded-lg w-full sm:w-full sm:max-w-md max-h-[80vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-gray-200">
                  Gift Card Details (#{withdraw.id})
                </h3>
              </div>
              
              <div className="p-4">
                {withdraw.option_request?.toLowerCase().includes("paypal") ? (
                  <p className="text-gray-300 mb-4">PayPal payment will be processed automatically.</p>
                ) : (
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Gift Card Code</label>
                      <input
                        type="text"
                        placeholder="Enter gift card code"
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onChange={(e) => setGiftCardData({...giftCardData, code: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Gift Card Pin</label>
                      <input
                        type="text"
                        placeholder="Enter gift card pin"
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onChange={(e) => setGiftCardData({...giftCardData, pin: e.target.value})}
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => executeAction('pay', giftCardData)}
                    disabled={!withdraw.option_request?.toLowerCase().includes("paypal") && giftCardData.code.length < 5}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Proceed
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlutoSingleWithdrawal;